import type { BuyDealFormState, OpsDocCategory, StripeWipeResultPayload, VoiceAudioCacheReason } from "./types";

export const TREND_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#a855f7",
  "#ef4444",
  "#0ea5e9",
  "#eab308",
  "#ec4899",
];


export const OTHER_COLOR = "#94a3b8";


export const VOICE_CACHE_REASON_LABELS: Record<VoiceAudioCacheReason, string> = {
  ttl: "TTL expired",
  max_entries: "Entry cap (LRU)",
  max_bytes: "Byte cap (LRU)",
  replaced: "Replaced",
  manual: "Manual invalidate",
  oversized: "Oversized (rejected)",
};


// Three-or-more errors in a single sweep almost always means something
// systemic (revoked GCS credentials, bucket renamed, etc.) rather than a
// transient one-off, so flag the count visually so the widget is
// impossible to miss at a glance.
export const MESSAGE_ATTACHMENT_FAILURE_THRESHOLD = 3;


// ─── Task #1282 — "Wipe Stripe Test Artifacts" admin tool ──────────────
// Test-mode-only Danger Zone tool for clearing accumulated Stripe IDs and
// per-event log rows so the local DB can be re-seeded against a fresh
// Stripe test fixture without leftover customer/subscription pointers
// breaking later runs. The button is gated behind a confirmation modal
// that requires the operator to type the exact phrase below — matches the
// pattern recommended in the Task #1282 spec ("wipe test data").
export const STRIPE_WIPE_CONFIRM_PHRASE = "wipe test data";


export const STRIPE_WIPE_RESULT_ROWS: Array<{
  key: keyof StripeWipeResultPayload["result"];
  label: string;
  kind: "updated" | "deleted";
}> = [
  { key: "appUsersUpdated", label: "app_users (Stripe IDs nulled)", kind: "updated" },
  { key: "paymentAccountsUpdated", label: "payment_accounts (Stripe IDs nulled)", kind: "updated" },
  { key: "vendorPayoutAccountsUpdated", label: "vendor_payout_accounts (Stripe IDs nulled)", kind: "updated" },
  { key: "vendorCreditCardsUpdated", label: "vendor_credit_cards (Stripe IDs nulled)", kind: "updated" },
  { key: "paymentTransactionsUpdated", label: "payment_transactions (Stripe IDs nulled)", kind: "updated" },
  { key: "vendorPayoutsDeleted", label: "vendor_payouts (deleted)", kind: "deleted" },
  { key: "billingProrationEventsDeleted", label: "billing_proration_events (deleted)", kind: "deleted" },
  { key: "billingSettlementErrorsDeleted", label: "billing_settlement_errors (deleted)", kind: "deleted" },
  { key: "referralHostAccrualsDeleted", label: "referral_host_accruals (deleted)", kind: "deleted" },
  { key: "referralHostCreditsDeleted", label: "referral_host_credits (deleted)", kind: "deleted" },
  { key: "processedStripeEventsDeleted", label: "processed_stripe_events (deleted)", kind: "deleted" },
];


export const INVOICE_STATUS_COLORS: Record<string, string> = {
  pending_payment_instructions: "bg-yellow-100 text-yellow-800",
  sent: "bg-blue-100 text-blue-800",
  overdue: "bg-red-100 text-red-700",
  paid: "bg-emerald-100 text-emerald-700",
  disputed: "bg-orange-100 text-orange-800",
  voided: "bg-gray-100 text-gray-500",
};


export const INVOICE_STATUS_LABELS: Record<string, string> = {
  pending_payment_instructions: "Pending Payment Instructions",
  sent: "Sent",
  overdue: "Overdue",
  paid: "Paid",
  disputed: "Disputed",
  voided: "Voided",
};


export const EMPTY_BUY_DEAL_FORM: BuyDealFormState = {
  address: "",
  city: "",
  state: "",
  zipCode: "",
  market: "",
  imageUrl: "",
  photos: "",
  purchasePrice: "",
  cashOnCash: "",
  monthlyCashFlow: "",
  capRate: "",
  currentBedrooms: "",
  currentBathrooms: "",
  futureBedrooms: "",
  futureBathrooms: "",
  sqft: "",
  yearBuilt: "",
  propertyType: "",
  arv: "",
  rehabCost: "",
  monthlyRent: "",
  monthlyExpenses: "",
  status: "preview",
  notes: "",
  listingUrl: "",
  isSample: true,
};


export const OPS_DOC_CATEGORY_LABEL: Record<OpsDocCategory, string> = {
  monitoring: "Monitoring",
  billing: "Billing",
  comms: "Comms",
  playbooks: "Playbooks",
};


// Task #861 — number of task badges to render inline before collapsing the
// rest into an "+N more" pill. Tuned so a release with up to 5 tasks
// stays single-line in the typical viewport.
export const TASK_BADGE_INLINE_LIMIT = 4;
